import React, { useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Dashboard as BoardIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  boardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  boardNameDisplay: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 6,
    cursor: "pointer",
    transition: "background 0.15s",
    userSelect: "none",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.04)",
    },
  },
  boardNameText: {
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  boardIcon: {
    color: theme.palette.primary.main,
    fontSize: 22,
  },
  select: {
    minWidth: 180,
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  templateOption: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": {
      borderColor: theme.palette.primary.light,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(0,0,0,0.02)",
    },
  },
  templateSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(33,150,243,0.12)"
        : "rgba(33,150,243,0.06)",
  },
  templateTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  templateDesc: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  visibilityHint: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: -4,
    marginLeft: 48,
  },
}));

const TEMPLATES = [
  {
    id: "status",
    title: "Por Status",
    desc: "3 colunas: Abertos, Pendentes, Fechados",
  },
  {
    id: "empty",
    title: "Em Branco",
    desc: "Painel vazio - configure suas colunas",
  },
];

const isCompanyBoard = (board) =>
  !!board &&
  (board.boardType === "global" ||
    board.isShared === true ||
    board.userId == null);

const BoardSelector = ({
  boards = [],
  currentBoardId,
  onSelectBoard,
  onCreateBoard,
  onRenameBoard,
  onDeleteBoard,
}) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [template, setTemplate] = useState("status");
  const [createIsGlobal, setCreateIsGlobal] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editIsGlobal, setEditIsGlobal] = useState(true);

  const currentBoard = boards.find((b) => b.id === currentBoardId);

  const handleOpenEdit = useCallback(() => {
    if (!currentBoard) return;
    setEditName(currentBoard.name);
    setEditIsGlobal(isCompanyBoard(currentBoard));
    setEditOpen(true);
  }, [currentBoard]);

  const handleConfirmEdit = useCallback(() => {
    const trimmed = editName.trim();
    if (!trimmed || !currentBoard) {
      setEditOpen(false);
      return;
    }

    const boardType = editIsGlobal ? "global" : "personal";
    const nameChanged = trimmed !== currentBoard.name;
    const visibilityChanged = editIsGlobal !== isCompanyBoard(currentBoard);

    if (nameChanged || visibilityChanged) {
      onRenameBoard(currentBoard.id, trimmed, { boardType });
    }
    setEditOpen(false);
  }, [editName, editIsGlobal, currentBoard, onRenameBoard]);

  const handleCreate = () => {
    if (!newName.trim()) return;
    onCreateBoard({
      name: newName.trim(),
      template,
      boardType: createIsGlobal ? "global" : "personal",
    });
    setDialogOpen(false);
    setNewName("");
    setTemplate("status");
    setCreateIsGlobal(true);
  };

  const handleDelete = () => {
    if (deleteConfirm) {
      onDeleteBoard(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const boardLabel = (b) => {
    const company = isCompanyBoard(b);
    return `${b.name}${company ? " (empresa)" : " (pessoal)"}`;
  };

  return (
    <div className={classes.root}>
      <div className={classes.boardHeader}>
        <BoardIcon className={classes.boardIcon} />

        {boards.length > 1 ? (
          <FormControl variant="outlined" size="small" className={classes.select}>
            <InputLabel>Painel</InputLabel>
            <Select
              value={currentBoardId || ""}
              onChange={(e) => onSelectBoard(e.target.value)}
              label="Painel"
            >
              {boards.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {boardLabel(b)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : currentBoard ? (
          <Tooltip title="Duplo clique para editar" placement="bottom">
            <div
              className={classes.boardNameDisplay}
              onDoubleClick={handleOpenEdit}
            >
              <span className={classes.boardNameText}>
                {boardLabel(currentBoard)}
              </span>
            </div>
          </Tooltip>
        ) : null}
      </div>

      <div className={classes.actionButtons}>
        {currentBoard && (
          <Tooltip title="Editar painel">
            <IconButton size="small" onClick={handleOpenEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Criar novo painel">
          <IconButton
            size="small"
            onClick={() => {
              setCreateIsGlobal(true);
              setDialogOpen(true);
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>

        {currentBoardId && boards.length > 1 && (
          <Tooltip title="Excluir painel">
            <IconButton
              size="small"
              onClick={() => setDeleteConfirm(currentBoardId)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* ── Edit Dialog ── */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Editar Painel</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            label="Nome do painel"
            variant="outlined"
            size="small"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConfirmEdit();
              }
            }}
            placeholder="Ex: Vendas, Suporte, Financeiro..."
          />

          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={editIsGlobal}
                  onChange={(e) => setEditIsGlobal(e.target.checked)}
                  color="primary"
                />
              }
              label="Painel da empresa (global)"
            />
            <Typography className={classes.visibilityHint}>
              {editIsGlobal
                ? "Visível para todos os usuários da empresa."
                : "Visível apenas para você."}
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirmEdit}
            color="primary"
            variant="contained"
            disabled={!editName.trim()}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Criar Novo Painel</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            label="Nome do painel"
            variant="outlined"
            size="small"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            placeholder="Ex: Vendas, Suporte, Financeiro..."
          />

          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={createIsGlobal}
                  onChange={(e) => setCreateIsGlobal(e.target.checked)}
                  color="primary"
                />
              }
              label="Painel da empresa (global)"
            />
            <Typography className={classes.visibilityHint}>
              {createIsGlobal
                ? "Padrão: visível para todos os usuários da empresa."
                : "Visível apenas para você."}
            </Typography>
          </div>

          <Typography variant="subtitle2" color="textSecondary">
            Template
          </Typography>

          {TEMPLATES.map((t) => (
            <div
              key={t.id}
              className={`${classes.templateOption} ${
                template === t.id ? classes.templateSelected : ""
              }`}
              onClick={() => setTemplate(t.id)}
            >
              <Typography className={classes.templateTitle}>
                {t.title}
              </Typography>
              <Typography className={classes.templateDesc}>
                {t.desc}
              </Typography>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCreate}
            color="primary"
            variant="contained"
            disabled={!newName.trim()}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
      >
        <DialogTitle>Excluir Painel</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este painel? Todas as colunas e
            configurações serão perdidas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BoardSelector;
